import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  ArrowLeft,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { invitationTemplates } from '../../utils/templates';
import useThemeStyles from '../../theme/useThemeStyles';

function Invitations() {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    groomName: '',
    brideName: '',
    date: '',
    time: '',
    venue: '',
    address: '',
    rsvpDate: '',
    rsvpEmail: '',
    additionalInfo: '',
    template: 'elegant',
  });

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const generateInvitation = () => {
    const template = invitationTemplates[formData.template];
    
    return `
      <div style="
        font-family: ${template.headerFont};
        color: ${template.color};
        background-color: ${template.backgroundColor};
        padding: ${template.padding};
        max-width: 600px;
        margin: 0 auto;
        text-align: center;
        border: ${template.borderWidth} ${template.borderStyle} ${template.borderColor};
      ">
        <h1 style="font-size: 3rem; margin-bottom: 1rem;">
          ${formData.groomName} & ${formData.brideName}
        </h1>
        
        <p style="font-size: 1.2rem; font-style: italic; margin-bottom: 2rem;">
          request the pleasure of your company at their wedding
        </p>
        
        <div style="margin: 2rem 0;">
          <p style="font-size: 1.1rem; margin: 0.5rem 0;">
            ${formData.date}
          </p>
          <p style="font-size: 1.1rem; margin: 0.5rem 0;">
            at ${formData.time}
          </p>
        </div>
        
        <div style="margin: 2rem 0;">
          <p style="font-size: 1.1rem; font-weight: bold; margin: 0.5rem 0;">
            ${formData.venue}
          </p>
          <p style="font-size: 1rem; margin: 0.5rem 0;">
            ${formData.address}
          </p>
        </div>
        
        ${formData.additionalInfo ? `
          <p style="font-size: 0.9rem; margin: 2rem 0; font-style: italic;">
            ${formData.additionalInfo}
          </p>
        ` : ''}
        
        <div style="margin-top: 3rem; font-size: 0.9rem;">
          <p>Please RSVP by ${formData.rsvpDate}</p>
          <p>${formData.rsvpEmail}</p>
        </div>
      </div>
    `;
  };

  const handlePreview = () => {
    const content = generateInvitation();
    const previewWindow = window.open('', '_blank');
    previewWindow.document.write(`
      <html>
        <head>
          <title>Invitation Preview</title>
          <style>
            body { margin: 0; padding: 20px; }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  const handleDownload = async () => {
    setLoading(true);
    const content = generateInvitation();
    const element = document.createElement('div');
    element.innerHTML = content;
    
    const options = {
      margin: 1,
      filename: 'wedding-invitation.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    try {
      await html2pdf().set(options).from(element).save();
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={styles.container.page}>
      <Box sx={styles.patterns.pageHeader}>
        <Box sx={styles.layout.flexRow} gap={2} alignItems="center">
          <Button
            startIcon={<ArrowLeft />}
            onClick={() => navigate('/stationary-gifts')}
            sx={{ minWidth: 'auto' }}
          >
            Back
          </Button>
          <Typography component="h2" className="pagename" sx={styles.text.pagename}>
            Wedding Invitations
          </Typography>
        </Box>
      </Box>

      <Typography variant="body1" sx={styles.text.sectionDescription} mb={3}>
        Create beautiful wedding invitations with our customizable templates
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Typography variant="h6" sx={styles.text.sectionTitle}>
                Invitation Details
              </Typography>
              
              <Box sx={styles.patterns.formContainer}>
                <FormControl fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={formData.template}
                    onChange={handleChange('template')}
                    label="Template"
                  >
                    <MenuItem value="elegant">Elegant</MenuItem>
                    <MenuItem value="modern">Modern</MenuItem>
                    <MenuItem value="rustic">Rustic</MenuItem>
                    <MenuItem value="floral">Floral</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Partner One's Name"
                  value={formData.groomName}
                  onChange={handleChange('groomName')}
                />

                <TextField
                  fullWidth
                  label="Partner Two's Name"
                  value={formData.brideName}
                  onChange={handleChange('brideName')}
                />

                <TextField
                  fullWidth
                  label="Wedding Date"
                  value={formData.date}
                  onChange={handleChange('date')}
                  placeholder="Saturday, June 15th, 2024"
                />

                <TextField
                  fullWidth
                  label="Wedding Time"
                  value={formData.time}
                  onChange={handleChange('time')}
                  placeholder="2:00 PM"
                />

                <TextField
                  fullWidth
                  label="Venue Name"
                  value={formData.venue}
                  onChange={handleChange('venue')}
                />

                <TextField
                  fullWidth
                  label="Venue Address"
                  value={formData.address}
                  onChange={handleChange('address')}
                  multiline
                  rows={2}
                />

                <TextField
                  fullWidth
                  label="RSVP Date"
                  value={formData.rsvpDate}
                  onChange={handleChange('rsvpDate')}
                  placeholder="Please RSVP by May 1st, 2024"
                />

                <TextField
                  fullWidth
                  label="RSVP Email"
                  value={formData.rsvpEmail}
                  onChange={handleChange('rsvpEmail')}
                  placeholder="rsvp@example.com"
                />

                <TextField
                  fullWidth
                  label="Additional Information"
                  value={formData.additionalInfo}
                  onChange={handleChange('additionalInfo')}
                  multiline
                  rows={3}
                  placeholder="Dress code, special instructions, etc."
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={styles.card.default}>
            <CardContent sx={styles.spacing.card}>
              <Typography variant="h6" sx={styles.text.sectionTitle}>
                Preview & Download
              </Typography>
              
              <Box sx={styles.layout.flexColumn} gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreview}
                  fullWidth
                >
                  Preview Invitation
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleDownload}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? 'Generating PDF...' : 'Download PDF'}
                </Button>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="body2" color="text.secondary">
                <strong>Tips:</strong>
                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                  <li>Preview your invitation before downloading</li>
                  <li>Use clear, readable fonts and colors</li>
                  <li>Include all essential information</li>
                  <li>Consider your wedding theme when choosing a template</li>
                </ul>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Invitations; 